import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ShoppingCart, Trash2, User, X, StickyNote, Save, Loader2, CreditCard } from 'lucide-react';
import { usePOSCartStore } from '../stores/posCartStore';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface CartProps {
  onCheckout: () => void;
  linkedTable?: string;
  onSave?: () => void;
  isSaving?: boolean;
  onPay?: () => void; // NEW: Direct payment
}

export const Cart = ({ onCheckout, linkedTable, onSave, isSaving, onPay }: CartProps) => {
  const {
    items,
    customerName,
    orderNotes,
    linkedTableNumber,
    updateQuantity,
    updateItemNote,
    removeItem,
    clearCart,
    setOrderNotes,
    getSubtotal,
    getVATAmount,
    getFiscalStamp,
    getTotal,
    getItemTotal,
  } = usePOSCartStore();

  const [showOrderNotes, setShowOrderNotes] = useState(false);

  const formatCurrency = (amount: number) => {
    return amount.toFixed(2) + ' DA';
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-emerald-600" />
            <span>الطلب / Commande</span>
          </h2>
          {items.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCart}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
            >
              <Trash2 className="w-4 h-4" />
              <kbd className="hidden sm:inline-flex text-[10px] font-mono bg-red-100 text-red-600 px-1.5 py-0.5 rounded">F9</kbd>
            </Button>
          )}
        </div>

        {/* Linked Table */}
        {linkedTableNumber && (
          <div className="mb-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm font-medium text-emerald-900">
              Table: {linkedTableNumber}
            </p>
          </div>
        )}

        {/* Customer */}
        {customerName && (
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <User className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">{customerName}</span>
          </div>
        )}
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
            <ShoppingCart className="w-20 h-20 mb-4 opacity-20" />
            <p className="text-center font-medium mb-1">السلة فارغة</p>
            <p className="text-center text-sm">Panier vide</p>
            <p className="text-center text-xs mt-4">
              اختر المنتجات لبدء البيع<br />
              Sélectionnez des produits pour commencer
            </p>
          </div>
        ) : (
          <div className="px-4 py-2 space-y-2">
            {items.map((item) => (
              <CartItemCard
                key={item.productId}
                item={item}
                onUpdateQuantity={(qty) => updateQuantity(item.productId, qty)}
                onUpdateNote={(note) => updateItemNote(item.productId, note)}
                onRemove={() => removeItem(item.productId)}
                itemTotal={getItemTotal(item.productId)}
                formatCurrency={formatCurrency}
              />
            ))}
          </div>
        )}
      </div>

      {/* Summary & Checkout */}
      {items.length > 0 && (
        <div className="border-t border-gray-200 p-4 space-y-3">
          {/* Order Notes */}
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowOrderNotes(!showOrderNotes)}
              className="w-full gap-2"
            >
              <StickyNote className="w-4 h-4" />
              Note de commande
            </Button>
            {showOrderNotes && (
              <Textarea
                value={orderNotes || ''}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Note générale (ex: Client pressé, table 5...)"
                className="mt-2 text-sm"
                rows={2}
              />
            )}
          </div>

          {/* Calculations */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Sous-total:</span>
              <span className="font-medium">{formatCurrency(getSubtotal())}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>TVA:</span>
              <span className="font-medium">{formatCurrency(getVATAmount())}</span>
            </div>
            {getFiscalStamp() > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Timbre fiscal:</span>
                <span className="font-medium">{formatCurrency(getFiscalStamp())}</span>
              </div>
            )}
            <div className="h-px bg-gray-300" />
            <div className="flex justify-between text-xl font-bold">
              <span>Total:</span>
              <span className="text-emerald-600">{formatCurrency(getTotal())}</span>
            </div>
          </div>

          {/* Checkout/Save/Pay Buttons */}
          {linkedTable && onSave ? (
            <div className="space-y-2">
              <Button
                onClick={onSave}
                disabled={isSaving}
                className="w-full h-14 text-lg font-bold bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Sauvegarder / حفظ
                  </>
                )}
              </Button>
              
              {onPay && (
                <Button
                  onClick={onPay}
                  variant="outline"
                  className="w-full h-12 text-base font-semibold border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 gap-2"
                >
                  <CreditCard className="w-4 h-4" />
                  Payer directement / الدفع
                  <kbd className="text-xs font-mono bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">F5</kbd>
                </Button>
              )}
            </div>
          ) : (
            <Button
              onClick={onCheckout}
              className="w-full h-14 text-lg font-bold bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 gap-2"
            >
              تأكيد الدفع / ENCAISSER
              <kbd className="text-xs font-mono bg-white/20 px-2 py-0.5 rounded">F12</kbd>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

interface CartItemCardProps {
  item: any;
  onUpdateQuantity: (qty: number) => void;
  onUpdateNote: (note: string) => void;
  onRemove: () => void;
  itemTotal: number;
  formatCurrency: (amount: number) => string;
}

const CartItemCard = ({
  item,
  onUpdateQuantity,
  onUpdateNote,
  onRemove,
  itemTotal,
  formatCurrency,
}: CartItemCardProps) => {
  const [showNote, setShowNote] = useState(!!item.notes);

  return (
    <div className="bg-muted border border-border rounded-lg p-3">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 text-sm line-clamp-2">
            {item.productName}
          </p>
          <p className="text-xs text-gray-500">
            Code: {item.productCode}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 ml-2"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex items-center justify-between mb-2">
        {/* Quantity Controls */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onUpdateQuantity(item.quantity - 1)}
            className="h-7 w-7 p-0"
          >
            -
          </Button>
          <span className="text-sm font-bold w-8 text-center">
            {item.quantity}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onUpdateQuantity(item.quantity + 1)}
            className="h-7 w-7 p-0"
          >
            +
          </Button>
        </div>

        {/* Price */}
        <div className="text-right">
          <p className="text-xs text-gray-600">
            {item.quantity} × {formatCurrency(item.unitPrice)}
          </p>
          <p className="font-bold text-emerald-600 text-sm">
            {formatCurrency(itemTotal)}
          </p>
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
          TVA {item.vat}%
        </span>
        {item.discount > 0 && (
          <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded">
            -{item.discount}
            {item.discountType === 'percentage' ? '%' : ' DA'}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowNote(!showNote)}
          className={cn(
            "h-5 px-2 text-xs",
            item.notes ? "text-orange-600" : "text-gray-500"
          )}
        >
          <StickyNote className="w-3 h-3" />
        </Button>
      </div>

      {/* Note Input */}
      {showNote && (
        <Textarea
          value={item.notes || ''}
          onChange={(e) => onUpdateNote(e.target.value)}
          placeholder="Note (ex: Sans oignons, bien cuit...)"
          className="text-xs"
          rows={2}
        />
      )}
    </div>
  );
};
